import { Link } from 'react-router-dom';
import { CircleAlert, Home, Lock, RotateCcw, ShieldAlert, Wrench } from 'lucide-react';
import type { ReactNode } from 'react';

import type { BanState } from '../../api/types';
import { apiErrorMessage } from '../../lib/errors';
import { useLocale } from '../../lib/useLocale';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { EmptyState } from '../ui/EmptyState';

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const { t } = useLocale();
  return (
    <EmptyState
      className={className}
      icon={<CircleAlert className="size-5" />}
      title={t('errors.title')}
      description={apiErrorMessage(error, t)}
      action={
        onRetry ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            leadingIcon={<RotateCcw className="size-3.5" />}
          >
            {t('common.retry')}
          </Button>
        ) : null
      }
    />
  );
}

export function NotFoundState({ title, description }: { title?: ReactNode; description?: ReactNode }) {
  const { t } = useLocale();
  return (
    <div className="mx-auto max-w-lg py-20">
      <EmptyState
        icon={<CircleAlert className="size-5" />}
        title={title ?? t('errors.notFound')}
        description={description ?? t('errors.notFoundBody')}
        action={
          <Button asChild variant="outline" size="sm" leadingIcon={<Home className="size-3.5" />}>
            <Link to="/">{t('errors.goHome')}</Link>
          </Button>
        }
      />
    </div>
  );
}

/** Shown when the server reports a feature flag as closed. */
export function FeatureClosed({
  title,
  description,
  className,
}: {
  title: ReactNode;
  description: ReactNode;
  className?: string;
}) {
  return (
    <Card className={className} tone="raised">
      <EmptyState icon={<Lock className="size-5" />} title={title} description={description} />
    </Card>
  );
}

export function MaintenanceScreen({ message, siteName }: { message: string; siteName: string }) {
  const { t } = useLocale();
  return (
    <div className="aurora grid-backdrop flex min-h-screen items-center justify-center px-4">
      <Card tone="glass" className="w-full max-w-lg p-10 text-center">
        <div className="bg-warning-soft text-warning mx-auto mb-5 flex size-14 items-center justify-center rounded-full">
          <Wrench className="size-6" />
        </div>
        <p className="eyebrow mb-2">{siteName}</p>
        <h1 className="text-2xl font-semibold">{t('errors.maintenance')}</h1>
        <p className="text-text-muted mt-3 text-sm">{message || t('errors.maintenanceBody')}</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-6"
          onClick={() => window.location.reload()}
          leadingIcon={<RotateCcw className="size-3.5" />}
        >
          {t('common.retry')}
        </Button>
      </Card>
    </div>
  );
}

/**
 * Ban notice. The server enforces the ban on every request; this only explains
 * to the player why their actions are refused.
 */
export function BanNotice({ ban }: { ban: BanState }) {
  const { t, dateTime } = useLocale();
  return (
    <div className="border-danger/30 bg-danger-soft text-danger flex items-start gap-3 border-b px-4 py-3 text-sm">
      <ShieldAlert className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0">
        <p className="font-medium">{t('errors.banned')}</p>
        <p className="mt-0.5 text-xs opacity-90">
          {ban.reason ? t('errors.bannedReason', { reason: ban.reason }) : t('ban.banner')}
          {' · '}
          {ban.isPermanent || !ban.endsAt
            ? t('errors.bannedPermanent')
            : t('errors.bannedUntil', { date: dateTime(ban.endsAt) })}
        </p>
      </div>
    </div>
  );
}
